import { aiGenerationService } from '../../services/ai/AIGenerationService';
import type { GenerationRequest, TemplateComponent } from '../../services/ai/types';
import { ComponentType } from '../../types';
import type { Component } from '../../types';
import { responsivePositionTranslator } from '../../services/responsive/ResponsivePositionTranslator';
import { enhanceComponentsWithChildren } from './editor-utils';
import type {
  CoreEditorSlice,
  EditorImmerGetState,
  EditorImmerSetState,
} from './editor-store-types';

type GeneratedComponentCandidate = TemplateComponent &
  Partial<
    Pick<
      Component,
      | 'name'
      | 'parentId'
      | 'order'
      | 'locked'
      | 'hidden'
      | 'version'
      | 'createdBy'
      | 'lastModifiedBy'
      | 'lastModifiedAt'
      | 'isDeleted'
      | 'createdAt'
      | 'updatedAt'
      | 'deletedAt'
      | 'isSubComponent'
      | 'subComponentType'
      | 'interactions'
      | 'metadata'
      | 'zIndex'
    >
  >;

function buildGeneratedComponent(
  component: GeneratedComponentCandidate,
  projectId: string,
  pageId: string,
  order: number,
): Component {
  const componentType = component.type as ComponentType;
  const now = new Date();

  return {
    ...component,
    type: componentType,
    id:
      component.id ||
      `comp_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
    projectId,
    pageId,
    name: component.name || `${componentType} Component`,
    properties: component.properties || {},
    position: component.position || { x: 0, y: 0, z: 0 },
    size: component.size || { width: '100%', height: 100 },
    style: component.style || {},
    responsivePosition:
      component.responsivePosition ||
      responsivePositionTranslator.generateDefaultResponsivePosition(
        componentType,
      ),
    children: component.children || [],
    order: component.order ?? order,
    locked: component.locked ?? false,
    hidden: component.hidden ?? false,
    version: component.version ?? 1,
    createdBy: component.createdBy ?? 'ai-generator',
    lastModifiedBy: component.lastModifiedBy ?? 'ai-generator',
    lastModifiedAt: component.lastModifiedAt ?? now,
    isDeleted: component.isDeleted ?? false,
    createdAt: component.createdAt ?? now,
    updatedAt: component.updatedAt ?? now,
  };
}

export interface GenerationSlice {
  // Generation progress state
  isGenerating: boolean;
  generationStage:
    | 'preparing'
    | 'generating'
    | 'finalizing'
    | 'complete'
    | 'error'
    | null;
  generationError: string | null;

  // AI Generation state (Task 6.3)
  isGeneratingPage: boolean;
  aiGenerationStage:
    | 'idle'
    | 'selecting'
    | 'generating'
    | 'finalizing'
    | 'complete'
    | 'error'
    | null;
  aiGenerationMetadata: {
    selectedTemplates: string[];
    tokensUsed: { step1: number; step2: number; total: number };
    latency: { step1: number; step2: number; total: number };
    confidence: number;
    reasoning: string;
  } | null;
  aiGenerationError: string | null;

  // AI Generation actions (Task 6.3)
  generatePageFromDescription: (
    description: string,
    constraints?: GenerationRequest['constraints'],
    context?: GenerationRequest['context'],
  ) => Promise<void>;
  setGenerationStage: (
    stage:
      | 'preparing'
      | 'generating'
      | 'finalizing'
      | 'complete'
      | 'error'
      | null,
  ) => void;
  setGenerationError: (error: string | null) => void;
  resetGenerationProgress: () => void;
  setAIGenerationStage: (
    stage: 'idle' | 'selecting' | 'generating' | 'complete' | 'error' | null,
  ) => void;
  setAIGenerationError: (error: string | null) => void;
  resetAIGenerationState: () => void;
}

export const generationInitialState: Pick<
  GenerationSlice,
  | 'isGenerating'
  | 'generationStage'
  | 'generationError'
  | 'isGeneratingPage'
  | 'aiGenerationStage'
  | 'aiGenerationMetadata'
  | 'aiGenerationError'
> = {
  // Generation progress initial state
  isGenerating: false,
  generationStage: null,
  generationError: null,

  // AI Generation initial state (Task 6.3)
  isGeneratingPage: false,
  aiGenerationStage: 'idle',
  aiGenerationMetadata: null,
  aiGenerationError: null,
};

export function createGenerationSlice<
  TState extends GenerationSlice & CoreEditorSlice,
>(
  set: EditorImmerSetState<TState>,
  get: EditorImmerGetState<TState>,
): GenerationSlice {
  return {
    ...generationInitialState,

    // Generation progress actions
    setGenerationStage: (stage) => {
      set((state) => {
        state.generationStage = stage;
        if (stage === 'preparing') {
          state.isGenerating = true;
          state.generationError = null;
        } else if (stage === 'complete' || stage === 'error') {
          state.isGenerating = false;
        }
      });
    },

    setGenerationError: (error) => {
      set((state) => {
        state.generationError = error;
        if (error) {
          state.generationStage = 'error';
          state.isGenerating = false;
        }
      });
    },

    resetGenerationProgress: () => {
      set((state) => {
        state.isGenerating = false;
        state.generationStage = null;
        state.generationError = null;
      });
    },

    // AI Generation actions (Task 6.3)
    generatePageFromDescription: async (description, constraints, context) => {
      const state = get();
      const projectId = state.project?.id;
      const pageId = state.currentPage?.id;

      if (!projectId) {
        console.error('❌ Cannot generate page: No project loaded');
        set((s) => {
          s.aiGenerationError =
            'No project loaded. Please create or open a project first.';
          s.aiGenerationStage = 'error';
        });
        return;
      }

      try {
        // Helper for visual delay between stages
        const delay = (ms: number) =>
          new Promise((resolve) => setTimeout(resolve, ms));

        // Reset state and start generation
        set((s) => {
          s.isGeneratingPage = true;
          s.aiGenerationStage = 'selecting';
          s.aiGenerationError = null;
          s.aiGenerationMetadata = null;
        });

        // Run API call with timed stage transitions
        // Stage 1: "Selecting" shows for 1.2s, then auto-transitions to "Generating"
        const apiPromise = aiGenerationService.generatePage(
          description,
          projectId,
          pageId,
          constraints,
          context,
        );

        // After 1.2s, switch to "Generating" stage while API continues
        const stageTimer = setTimeout(() => {
          set((s) => {
            if (s.aiGenerationStage === 'selecting') {
              s.aiGenerationStage = 'generating';
            }
          });
        }, 1200);

        // Wait for API to complete
        const result = await apiPromise;
        clearTimeout(stageTimer);

        // Ensure we're at least on "Generating" stage
        set((s) => {
          if (s.aiGenerationStage === 'selecting') {
            s.aiGenerationStage = 'generating';
          }
        });

        if (result.success && result.page && result.metadata) {
          // Store metadata
          set((s) => {
            s.aiGenerationMetadata = result.metadata || null;
          });

          // Visual delay for generating stage (minimum display time)
          await delay(1000);

          // Step 3: Finalizing (combines layout optimization and visual styling)
          set((s) => {
            s.aiGenerationStage = 'finalizing';
          });

          // Transform components for editor compatibility
          const transformedComponents =
            aiGenerationService.transformComponentsForEditor(
              result.page.components,
            );

          // Visual delay for finalizing stage
          await delay(1000);

          // Add components to current page
          set((s) => {
            const currentPageId = s.currentPage?.id;
            if (currentPageId) {
              // Clear existing components on the page
              s.components = [];
              s.componentMap.clear();

              // Add new generated components
              transformedComponents.forEach((comp, index) => {
                const newComponent = buildGeneratedComponent(
                  comp,
                  projectId,
                  currentPageId,
                  index,
                );

                s.components.push(newComponent);
                s.componentMap.set(newComponent.id, newComponent);
              });

              // Update page components map
              s.pageComponents.set(currentPageId, [...s.components]);

              // Enhance components with _childComponents for FlexContainers
              const enhanced = enhanceComponentsWithChildren([...s.components]);
              s.components = enhanced;
              s.componentMap.clear();
              enhanced.forEach((comp) => s.componentMap.set(comp.id, comp));
              s.pageComponents.set(currentPageId, enhanced);

              // Update history
              s.history.past.push([...s.history.present]);
              s.history.present = [...s.components];
              s.history.future = [];

              // Mark as modified
              s.isModified = true;
              s.lastUpdateTimestamp = Date.now();
            }

            // Update generation state
            s.isGeneratingPage = false;
            s.aiGenerationStage = 'complete';
          });
        } else {
          // Handle generation failure
          const userFriendlyMessage =
            aiGenerationService.getUserFriendlyErrorMessage(
              result.error?.code || 'UNKNOWN_ERROR',
            );

          console.error('❌ Page generation failed', {
            error: result.error,
            userFriendlyMessage,
          });

          set((s) => {
            s.isGeneratingPage = false;
            s.aiGenerationStage = 'error';
            s.aiGenerationError = userFriendlyMessage;
          });
        }
      } catch (error: unknown) {
        console.error('❌ Page generation error', error);
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred during generation';

        set((s) => {
          s.isGeneratingPage = false;
          s.aiGenerationStage = 'error';
          s.aiGenerationError = errorMessage;
        });
      }
    },

    setAIGenerationStage: (stage) => {
      set((state) => {
        state.aiGenerationStage = stage;
        if (stage === 'selecting') {
          state.isGeneratingPage = true;
          state.aiGenerationError = null;
        } else if (
          stage === 'complete' ||
          stage === 'error' ||
          stage === 'idle'
        ) {
          state.isGeneratingPage = false;
        }
      });
    },

    setAIGenerationError: (error) => {
      set((state) => {
        state.aiGenerationError = error;
        if (error) {
          state.aiGenerationStage = 'error';
          state.isGeneratingPage = false;
        }
      });
    },

    resetAIGenerationState: () => {
      set((state) => {
        state.isGeneratingPage = false;
        state.aiGenerationStage = 'idle';
        state.aiGenerationMetadata = null;
        state.aiGenerationError = null;
      });
    },
  };
}
